<template>
  <div class="language-switcher-container" ref="switcherContainer">
    <!-- Button that displays the current language and now opens the modal -->
    <button
      @click="openModal"
      :disabled="loading"
      class="language-switcher-button"
      aria-haspopup="dialog"
    >
      <span class="selected-language">
        <span class="flag" :class="currentLocaleObject.flagClass"></span>
        <span v-if="viewLabel" class="language-label">
          {{ currentLocaleObject.label }}
        </span>
      </span>
      <svg
        class="dropdown-arrow"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    <!-- Language Modal for Mobile-first experience -->
    <transition name="modal-fade">
      <div v-if="isModalOpen" class="modal-backdrop" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">{{ $t('languageSwitch.title') }}</h3>
            <button
              aria-label="Close modal"
              class="close-button"
              @click="closeModal"
            >
              &times;
            </button>
          </div>
          <ul class="language-options-list">
            <li
              v-for="locale in supportedLocales"
              :key="locale.value"
              @click="onLanguageSelected(locale.value)"
              class="language-option"
              :class="{ 'is-selected': locale.value === currentLanguage }"
              role="button"
            >
              <span class="flag" :class="locale.flagClass"></span>
              <span class="language-label">{{ locale.label }}</span>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import LocalStorage from '@/services/LocalStorage';
import UserService from '@/services/AccountHttp';

export default {
  props: {
    viewLabel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentLanguage: this.$i18n.locale,
      supportedLocales: [],
      loading: false,
      isModalOpen: false, // Controls modal visibility
    };
  },
  watch: {
    // Watch for modal state changes to prevent background scrolling
    isModalOpen(isOpen) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  computed: {
    // Returns the full object for the current locale for easy access in the template
    currentLocaleObject() {
      const defaultLocale = {
        value: 'pt-BR',
        label: 'Português',
        flagClass: 'flag__br',
      };
      return (
        this.supportedLocales.find((l) => l.value === this.currentLanguage) ||
        defaultLocale
      );
    },
  },
  methods: {
    openModal() {
      if (!this.loading) {
        this.isModalOpen = true;
      }
    },
    closeModal() {
      this.isModalOpen = false;
    },
    async onLanguageSelected(newLanguage) {
      if (newLanguage === this.currentLanguage) {
        this.closeModal();
        return;
      }

      this.loading = true;
      this.closeModal();

      this.currentLanguage = newLanguage;
      this.$i18n.locale = newLanguage;
      LocalStorage.set('language', newLanguage);
      this.$emit('change', newLanguage);

      try {
        const result = await UserService.setErrorHandler((error) => {
          throw error;
        }).updateLanguage(newLanguage);

        if (result) {
          console.log('Updated language in backend:', newLanguage);
        }
      } catch (error) {
        console.error('Failed to update language in backend:', error);
      } finally {
        this.loading = false;
        window.location.reload();
      }
    },
    async loadStoredLanguage() {
      const storedLanguage = LocalStorage.get('language');
      const defaultLanguage = 'pt-BR';

      if (storedLanguage) {
        this.currentLanguage = storedLanguage;
        this.$i18n.locale = storedLanguage;
        return;
      }

      try {
        const user = await UserService.getUser();
        const userLanguage = user.data.lang || defaultLanguage;
        this.currentLanguage = userLanguage;
        this.$i18n.locale = userLanguage;
        LocalStorage.set('language', userLanguage);
      } catch (error) {
        console.error('Failed to fetch user language from backend:', error);
        this.currentLanguage = defaultLanguage;
        this.$i18n.locale = defaultLanguage;
      }
    },
    loadSupportedLocales() {
      const envLocales =
        process.env.VUE_APP_118N_SUPPORTED_LOCALES || 'pt-BR,en,es';

      const localesArray = envLocales.split(',').map((locale) => locale.trim());

      const localeMap = {
        'pt-BR': { value: 'pt-BR', label: 'Português', flagClass: 'flag__br' },
        en: { value: 'en', label: 'English', flagClass: 'flag__us' },
        es: { value: 'es', label: 'Español', flagClass: 'flag__es' },
      };

      this.supportedLocales = localesArray
        .filter((locale) => localeMap[locale])
        .map((locale) => ({
          ...localeMap[locale],
        }));
    },
  },
  mounted() {
    this.loadSupportedLocales();
    this.loadStoredLanguage();
  },
  beforeDestroy() {
    // Clean up body style if component is destroyed while modal is open
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* Main container */
.language-switcher-container {
  position: relative;
  display: inline-block;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, Arial, sans-serif;
}

/* Main button */
.language-switcher-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
  min-width: 130px;
  text-align: left;
}
.language-switcher-button:hover {
  border-color: #cbd5e0;
}
.selected-language,
.language-label {
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
}
.dropdown-arrow {
  color: #718096;
}

/* Modal styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 400px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 15px;
  margin-bottom: 15px;
}
.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}
.close-button {
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  color: #718096;
  cursor: pointer;
  padding: 0;
}

/* Language list inside modal */
.language-options-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.language-option {
  display: flex;
  align-items: center;
  padding: 12px 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 6px;
  font-size: 16px;
}
.language-option:hover {
  background-color: #f7fafc;
}
.language-option.is-selected {
  font-weight: 600;
  background-color: #edf2f7;
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-active .modal-content,
.modal-fade-leave-active .modal-content {
  transition: all 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .modal-content,
.modal-fade-leave-to .modal-content {
  transform: translateY(-20px);
}

/* Flag styles */
.flag {
  display: inline-block;
  width: 24px;
  height: 16px;
  margin-right: 12px;
  background-image: url('https://cdn.assine.online/assets/images/flags.png');
  background-repeat: no-repeat;
  background-size: auto;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
}

.flag__br {
  background-position: -679px 0;
}
.flag__us {
  background-position: -5262px 1px;
}
.flag__es {
  background-position: -1554px 0;
}
</style>
